import  { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {Plus} from "lucide-react";
import ClipLoader from "react-spinners/ClipLoader";
import EditorViewer from "./RTEEditorViewer";
import { useParams } from "react-router-dom";
import PostActions from "../../components/LikeCommentActions";
import DropdownActions from "../../components/DropdownActions";
import CodeHighlighter from "../../components/CodeHighlighter";
import DOMPurify from 'dompurify';
const BASE_URL = process.env.REACT_APP_BASE_URL;

const Code = () => {
  const token = localStorage.getItem("access_token");
  const { userId: id } = useParams();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [initialCode, setInitialCode] = useState("");
  const [gitLink, setGitLink] = useState("");
  const [media, setMedia] = useState(null);
  const [snippets, setSnippets] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editLanguage, setEditLanguage] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editGitLink, setEditGitLink] = useState("");
  const [editMedia, setEditMedia] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewCode, setViewCode] = useState(false);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [visibility, setVisibility] = useState("public");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsAddModalOpen(false);
        setIsEditModalOpen(false);
        setViewCode(false);
        setInitialCode("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/profile/create-profile/`, {
        headers: { Authorization: `Bearer ${token}`,  "x-api-key": process.env.REACT_APP_X_API_KEY, },
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentUserId(data.data?.user);
        localStorage.setItem("user_id", data.data?.user);
      } else {
        console.error("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error getting user profile.");
    }
  }, [BASE_URL, token]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const fetchSnippets = async () => {
    setLoading(true);
    try {
      let snippetsFromApi = [];
      if (id) {
        const res = await axios.get(
          `${BASE_URL}/user/profile/all-profiles?id=${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        snippetsFromApi = res.data?.data?.user_codes || [];
      } else {
        const res = await axios.get(`${BASE_URL}/user/code/code/`, {
          headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        });
        snippetsFromApi = res.data.results || [];
      }
      const normalizedSnippets = snippetsFromApi.map((snippet) => ({
        ...snippet,
        is_Liked: snippet.is_liked,
        likes_count: snippet.total_likes || 0,
      }));
      setSnippets(normalizedSnippets);
    } catch (err) {
      console.error("Failed to fetch snippets", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnippets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanHtml = DOMPurify.sanitize(code);
    // console.log("The new code is:",cleanHtml)
    if(!title)
    {
      toast.error('Code title required')
      return;
    }
    if(!language)
    {
      toast.error("Code language required")
      return;
    }
    if(!cleanHtml)
    {
      toast.error("Code Required")
      return;
    }
    const token = localStorage.getItem("access_token");
    const formData = new FormData();
    formData.append("title", title);
    formData.append("code_of", language);
    formData.append("description", description);
    formData.append("code", cleanHtml);
    formData.append("git_link", gitLink);
    formData.append("visibility", visibility);
    if (media) {
      formData.append("media", media);
    }
    try {
      setSubmitLoading(true);
      await axios.post(`${BASE_URL}/user/code/code/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
      });
      toast.success("Snippet added!");
      fetchSnippets();
      setTitle("");
      setLanguage("");
      setDescription("");
      setCode("");
      setGitLink("");
      setMedia(null);
      setIsAddModalOpen(false);
      setInitialCode("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error(error);
      toast.error("Upload failed.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const openEditModal = (snippet) => {
    setEditingSnippet(snippet);
    setEditTitle(snippet.title || "");
    setEditLanguage(snippet.code_of || "");
    setEditDescription(snippet.description || "");
    setCode(snippet.code || "");
    setInitialCode(snippet.code || "");
    setEditGitLink(snippet.git_link || "");
    setEditMedia(null);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setInitialCode("");
    setEditingSnippet(null);
  };

  const handleUpdateSnippet = async (id) => {

    const token = localStorage.getItem("access_token");
    const formData = new FormData();
    const cleanHtml = DOMPurify.sanitize(code);
    formData.append("id", id);
    formData.append("title", editTitle);
    formData.append("code_of", editLanguage);
    formData.append("description", editDescription);
    formData.append("code", cleanHtml);
    formData.append("git_link", editGitLink);
    formData.append("visibility", visibility);
    if (editMedia) formData.append("media", editMedia);

    try {
      setSubmitLoading(true);
      await axios.patch(`${BASE_URL}/user/code/code/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
      });
      toast.success("Snippet updated!");
      fetchSnippets();
      closeEditModal();
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteSnippet = async (id) => {
    const token = localStorage.getItem("access_token");
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URL}/user/code/code/`, {
        headers: { Authorization: `Bearer ${token}`,"x-api-key": process.env.REACT_APP_X_API_KEY },
        data: { id },
      });
      toast.success("Snippet deleted!");
      fetchSnippets();
      closeEditModal();
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };
 const handleVisibilityChange = (e) => {
    setVisibility(e.target.value);
  };
  return (
    <>
      <div className="w-full flex flex-col items-center justify-center">
         <div className="mb-5 w-full max-w-[1050px] flex justify-end">
          {!id && (
              <button
                className="ml-4 shrink-0 bg-[#004182] text-white p-3 rounded-full font-medium cursor-pointer border border-[#00449d] flex items-center justify-center"
                onClick={() => setIsAddModalOpen(true)}
              >
                <Plus size={18}></Plus>
              </button>
            )}
         </div>
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            {" "}
            <ClipLoader color="#4A90E2" size={40} />
          </div>
        ) : snippets.length === 0 ? (
          <div className="w-full max-w-[1050px] flex flex-col justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
  <div className="w-16 h-16 bg-[#e8f0f7] rounded-xl flex items-center justify-center mb-4">
    <svg 
      className="w-8 h-8 text-[#0f6abf]" 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" 
      />
    </svg>
  </div>
  <h3 className="text-lg font-semibold text-gray-900 mb-2">No code snippets yet</h3>
  <p className="text-gray-500 text-sm mb-4">Share your first code snippet with the community</p>
</div>
        ) : (
      
  
   <div className="w-full max-w-[1050px]  mx-auto space-y-4">
  {snippets.map((item) => (
    <div
      key={item.id}
      className="relative bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-[#0f6abf]/20 transition-all duration-200"
    >
      {/* Header */}
     <div className="relative flex items-center justify-between p-2 md:p-6 border-b border-gray-100">
  {/* Left: Icon + Title */}
  <div className="flex items-center gap-3 min-w-0">
    <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center flex-shrink-0">
      <svg
        className="w-5 h-5 text-[#0f6abf]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
        />
      </svg>
    </div>

    {/* Title */}
    <h4 className="text-lg font-bold text-gray-900 max-w-[calc(100%-1rem)]">
      {item.title}
    </h4>
  </div>

  {/* Right: Dropdown (absolute) */}
  {!id && (
    <div className="absolute top-[-25px] right-0">
      <DropdownActions
        onEdit={() => openEditModal(item)}
        onDelete={() => handleDeleteSnippet(item.id)}
      />
    </div>
  )}
</div>


      {/* Body */}
      <div className="p-2 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
          <p className="text-sm font-medium text-gray-700">
            Language: <span className="text-gray-900">{item.code_of}</span>
          </p>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
      </div>

      {/* Links */}
      <div className="flex gap-3 px-6 mt-3">
        {item.git_link && (
          <a
            href={item.git_link}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            GitHub
          </a>
        )}
        {item.code && (
          <button
            onClick={() => {
              setInitialCode(item.code);
              setViewCode(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#0f6abf] text-white text-sm font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Code
          </button>
        )}
      </div>

      {/* Post Actions */}
      <div className="p-2 md:p-6">
        <PostActions
          postId={item.id}
          type="code"
          is_Liked={item.is_liked === true}
          likeCount={item.total_likes}
          currentUser={currentUserId}
        />
      </div>
    </div>
  ))}
</div>


        )}
      </div>

    {isAddModalOpen && (
  <div className="mt-[160px] mb-8 md:mb-0 md:mt-[80px] fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div
      ref={modalRef}
      className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl"
    >
      {/* Modal Header */}
      <div className="p-2 md:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Add Code Snippet</h3>
          </div>
          <button
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
            onClick={() => {
              setIsAddModalOpen(false);
              setInitialCode("");
            }}
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal Content */}
      <div className="p-2 md:p-6">
          <form className="p-2 md:p-6 max-h-[70vh] overflow-y-auto" onSubmit={handleSubmit}>
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              placeholder="Title of Code Snippet"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Tech/Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Technology</label>
            <input
              type="text"
              placeholder="e.g. JavaScript, Python, React"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              placeholder="Enter a brief description of the code..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all resize-y"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></textarea>
          </div>

          {/* Code Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Code</label>
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <EditorViewer code={true} readOnly={false} value={initialCode} setValue={setCode} />
            </div>
          </div>

          {/* GitHub URL */}
          <div className="mb-2.5">
            <label className="block text-sm font-medium text-gray-700 mb-2">GitHub URL</label>
            <input
              type="text"
              placeholder="https://github.com/username/repository"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              value={gitLink}
              onChange={(e) => setGitLink(e.target.value)}
            />
          </div>
            <div className="mb-2.5">
            <label className="block font-semibold mb-1.5 text-sm text-gray-700">Sharing Options</label>
            <select 
              className="w-full h-[50px] px-4 py-2.5 border border-gray-300 rounded-xl bg-white outline-none text-gray-900 text-sm font-medium focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              value={visibility}
              onChange={handleVisibilityChange}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
          {/* File Upload */}
          <div className="mb-2.5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload File</label>
            <input
              type="file"
              id="snippetFile"
              ref={fileInputRef}
              onChange={(e) => setMedia(e.target.files[0])}
              accept=".py,.js,.html,.css,.java,.txt,.ts,.jsx,.tsx,.json,.md"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#e8f0f7] file:text-[#0f6abf] hover:file:bg-[#d0e0f0] transition-all"
            />
          </div>

          {/* Actions */}
          <div className="pt-6 border-t border-gray-100">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setInitialCode("");
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitLoading}
                className="px-5 py-2.5 bg-[#0f6abf] text-white font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {submitLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Save Snippet
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

 {isEditModalOpen && (
  <div className="fixed inset-0 z-[999] bg-black/50 flex items-center justify-center p-4">
    <div
      className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl"
      ref={modalRef}
    >
      {/* Modal Header */}
      <div className="p-2 md:p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Edit Code Snippet</h3>
              <p className="text-sm text-gray-500 mt-0.5">Update your code snippet details</p>
            </div>
          </div>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => {
              setIsEditModalOpen(false);
              setInitialCode("");
            }}
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal Content */}
      <div className="p-2 md:p-6 max-h-[70vh] overflow-y-auto">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateSnippet(editingSnippet.id);
          }}
        >
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Enter snippet title"
            />
          </div>

          {/* Tech & Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Technology</label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
                placeholder="e.g., JavaScript, Python"
                value={editLanguage}
                onChange={(e) => setEditLanguage(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">GitHub Link</label>
              <input
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
                value={editGitLink}
                onChange={(e) => setEditGitLink(e.target.value)}
                placeholder="https://github.com/..."
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all min-h-[100px] resize-y"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Describe what this code does..."
            />
          </div>

          {/* Code Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Code</label>
            <div className="border border-gray-300 rounded-xl overflow-hidden">
              <EditorViewer
                readOnly={false}
                value={initialCode}
                setValue={setCode}
                code={true}
              />
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload File</label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#0f6abf] transition-colors">
              <input
                id="editSnippetFile"
                type="file"
                className="hidden"
                onChange={(e) => setEditMedia(e.target.files[0])}
                accept=".py,.js,.html,.css,.java,.txt"
              />
              <label htmlFor="editSnippetFile" className="cursor-pointer">
                <div className="w-12 h-12 bg-[#e8f0f7] rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm text-gray-600">Click to upload new file</p>
                <p className="text-xs text-gray-400 mt-1">.py, .js, .html, .css, .java, .txt</p>
              </label>
            </div>
          </div>
            <div className="mb-2.5">
            <label className="block font-semibold mb-1.5 text-sm text-gray-700">Sharing Options</label>
            <select 
              className="w-full h-[50px] px-4 py-2.5 border border-gray-300 rounded-xl bg-white outline-none text-gray-900 text-sm font-medium focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              value={visibility}
              onChange={handleVisibilityChange}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>

          {/* Current File */}
          {editingSnippet?.media && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-gray-600">Current File:</p>
                <a
                  href={editingSnippet.media}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-[#0f6abf] hover:underline"
                >
                  View file
                </a>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-5 py-2.5 bg-red-100 text-red-600 font-medium rounded-xl hover:bg-red-200 transition-colors flex items-center gap-2"
              onClick={() => handleDeleteSnippet(editingSnippet.id)}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
              Delete
            </button>
            
            <button
              type="submit"
              className="px-5 py-2.5 bg-[#0f6abf] text-white font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors flex items-center gap-2"
              disabled={submitLoading}
            >
              {submitLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Update Snippet
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

      {viewCode && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999] p-4">
    <div 
      ref={modalRef} 
      className="relative bg-white rounded-2xl p-2 md:p-6 max-w-4xl w-full  shadow-xl"
    >
      {/* Close Button */}
      <button
        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
        onClick={() => {
          setViewCode(false);
          setInitialCode("");
        }}
      >
        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">View Code</h3>
          <p className="text-sm text-gray-500 mt-0.5">Review the code snippet</p>
        </div>
      </div>

      {/* Editor */}
   <div className="w-full  mx-auto">
  <pre className="min-h-[40vh] max-w-7xl max-h-[80vh] overflow-auto">
    <CodeHighlighter codeHtml={initialCode} />
  </pre>
</div>



    
    </div>
  </div>
)}
    </>
  );
};

export default Code;
